import {
  Card,
  Separator,
  ButtonGroup,
  Button,
  Tabs,
  DataStatus,
  TabProps,
  closeCurrentDialog,
  openDialog,
} from 'platform/components';
import {HStack, Box, VStack, Show, Heading} from 'platform/foundation';

import {RefObject, useState} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetTireVehicleCountQuery} from '@dms/api/core';
import {useGetAuthorizationProfileHasIntegrationQuery} from '@dms/api/security';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceCaseIntegrationsQuery,
  usePostServiceCaseAddServicePackageMutation,
  useLazyGetServiceCaseJobsQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError, queryParams, useTenant} from '@dms/shared';
import {JobFormRef} from '@dms/shared-aftersales';

import {buildArray, useQueryState} from 'shared/web';

import {ExternalPackagesDialog} from '../../../../components/ExternalPackagesDialog';
import {ServicePackages} from '../../../../components/ServicePackages/ServicePackages';
import {DAIMLER_INTEGRATION_CODE} from '../../../../constants/daimlerIntegrationCode';
import {XENTRY_INTEGRATION_CODE} from '../../../../constants/xentryIntegrationCode';
import {useWorkshopUrl} from '../../../../hooks/useWorkshopUrl';
import {HistoryOfRepairs} from './components/HistoryOfRepairs';
import {Jobs} from './components/Jobs';
import {Tires} from './components/Tires/Tires';
import {XentryIntegration} from './components/XentryIntegration';

interface ServiceJobsSelectionProps {
  isBackButtonDisabled: boolean;
  onBackClick: () => void;
  formRef?: RefObject<JobFormRef | null>;
}

const HYUNDAI_SERVICE_INSPECTIONS_CZ_INTEGRATION_CODE = 'HYUNDAI_SERVICE_INSPECTIONS_CZ';

export function ServiceJobsSelection(props: ServiceJobsSelectionProps) {
  const {serviceCaseId, orderId} = useWorkshopUrl();
  const {tenant} = useTenant();
  const [activeTabId, setActiveTabId] = useState<string>('jobs');

  const handleTabChange = (tabId: string) => {
    setActiveTabId(tabId);
  };

  const [postServiceCaseAddServicePackage] = usePostServiceCaseAddServicePackageMutation();
  const [fetchJobs] = useLazyGetServiceCaseJobsQuery();
  const [, setOpenJobId] = useQueryState(queryParams.SERVICE_CASE_OPEN_JOB_ID);
  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId},
    {skip: isNilOrEmpty(serviceCaseId)}
  );
  const {data: tireVehicleCount} = useGetTireVehicleCountQuery(
    {vehicleId: serviceCase?.vehicleId ?? ''},
    {skip: isNilOrEmpty(serviceCase?.vehicleId)}
  );
  const {data: integrations} = useGetServiceCaseIntegrationsQuery(
    {serviceCaseId},
    {skip: isNilOrEmpty(serviceCaseId)}
  );
  const {data: authorizationProfileHasIntegrationData} =
    useGetAuthorizationProfileHasIntegrationQuery(
      {
        authorizationProfileId: serviceCase?.authorizationProfileId ?? '',
        'x-tenant': tenant?.id ?? null,
      },
      {
        skip: isNilOrEmpty(serviceCase?.authorizationProfileId) || isNilOrEmpty(tenant?.id),
      }
    );
  const isHyundaiServiceInspectionsCzIntegrationEnabled = Boolean(
    authorizationProfileHasIntegrationData?.integration?.some(
      (integration) => integration?.code === HYUNDAI_SERVICE_INSPECTIONS_CZ_INTEGRATION_CODE
    )
  );

  const handleAddServicePackage = async (servicePackageId: string) => {
    await postServiceCaseAddServicePackage({
      serviceCaseId,
      body: {servicePackage: {servicePackageId}},
    })
      .unwrap()
      .then(async () => {
        const result = await fetchJobs({serviceCaseId});
        const jobs = result.data;
        const lastJob = jobs?.[jobs.length - 1];
        if (lastJob?.id) {
          setOpenJobId(lastJob.id);
        }
      })
      .catch(handleApiError);
  };

  const openExternalPackagesDialog = () => {
    openDialog(
      <ExternalPackagesDialog
        serviceCaseId={serviceCaseId}
        serviceOrderId={orderId}
        onClose={closeCurrentDialog}
        data-testid={testIds.workshop.createServiceCase('externalPackages')}
      />,
      {
        title: i18n.t('entity.integration.labels.externalPackages'),
        size: 'xLarge',
        withAdditionalFooter: true,
        disableBodyPadding: true,
      }
    );
  };

  return (
    <VStack spacing={4} height="100%">
      <Show
        when={integrations?.caseIntegration?.some(
          (integration) =>
            integration?.integration?.code === XENTRY_INTEGRATION_CODE ||
            integration?.integration?.code === DAIMLER_INTEGRATION_CODE
        )}
      >
        <XentryIntegration
          serviceCaseId={serviceCaseId}
          data-testid={testIds.workshop.createServiceCase('xentry')}
        />
      </Show>
      <Card isFullHeight>
        <DataStatus isLoading={isNilOrEmpty(serviceCaseId)} minHeight="100%">
          <HStack grow={1} height="100%">
            <Box minWidth={80}>
              <Box paddingBottom={4}>
                <HStack justify="space-between" align="center">
                  <Heading size={4}>
                    {i18n.t('entity.workshopServicePackages.labels.title')}
                  </Heading>
                  <Show when={isHyundaiServiceInspectionsCzIntegrationEnabled}>
                    <Button
                      title={i18n.t('entity.integration.labels.externalPackages')}
                      variant="secondary"
                      leftIcon="content/save_alt"
                      onClick={openExternalPackagesDialog}
                      data-testid={testIds.workshop.createServiceCase('externalPackages')}
                    />
                  </Show>
                </HStack>
              </Box>
              <ServicePackages
                serviceCaseId={serviceCaseId}
                isWithoutTitle
                onServicePackageAdd={handleAddServicePackage}
                data-testid={testIds.workshop.createServiceCase('packages')}
              />
            </Box>
            <Separator orientation="vertical" />
            <Box flex={1}>
              <Tabs
                activeTabId={activeTabId}
                onChange={handleTabChange}
                tabs={buildArray<TabProps>([
                  {
                    id: 'jobs',
                    content: <Jobs serviceCaseId={serviceCaseId} formRef={props.formRef} />,
                    title: i18n.t('page.workshop.labels.requests'),
                  },
                  {
                    id: 'historyOfRepairs',
                    content: (
                      <HistoryOfRepairs
                        vehicleId={serviceCase?.vehicleId!} // eslint-disable-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
                        onBack={() => handleTabChange('jobs')}
                      />
                    ),
                    title: i18n.t('entity.serviceCase.labels.historyOfRepairs'),
                  },
                ]).whenNot(isNilOrEmpty(serviceCase?.vehicleId), () => ({
                  id: 'tires',
                  content: <Tires vehicleId={serviceCase?.vehicleId ?? ''} />,
                  title: i18n.t('page.tiresInventory.labels.tires'),
                  isDisabled: (tireVehicleCount?.count ?? 0) === 0,
                }))}
                variant="condensed"
                data-testid={testIds.workshop.createServiceCase('tabs')}
              />
            </Box>
          </HStack>
        </DataStatus>
      </Card>
      <ButtonGroup align="right">
        <Button
          onClick={props.onBackClick}
          variant="outlined"
          data-testid={testIds.workshop.createServiceCase('back')}
          title={i18n.t('general.actions.back')}
          isDisabled={props.isBackButtonDisabled}
        />
      </ButtonGroup>
    </VStack>
  );
}
